import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";
import DailySchedule from "@COMPONENTS/product/pages/detailPackage/desktop/schedule/DailySchedule";
import Episode from "@COMPONENTS/product/pages/detailPackage/desktop/schedule/Episode";
import Review from "@COMPONENTS/product/pages/detailPackage/desktop/schedule/Review";
import Information from "@COMPONENTS/product/pages/detailPackage/desktop/schedule/Information";
import Option from "@COMPONENTS/product/pages/detailPackage/desktop/schedule/Option";
import {
	Importance,
	Insurance,
	Notice,
	PassportandVisa,
	Refer,
	Safety,
} from "@COMPONENTS/product/pages/detailPackage/desktop/schedule/ReferTabs";
import Shopping from "@COMPONENTS/product/pages/detailPackage/desktop/schedule/Shopping";
import {
	Accommodation,
	Enjoy,
	Food,
	TourlistDestination,
} from "@COMPONENTS/product/pages/detailPackage/desktop/schedule/TemplateTabs";
import * as style from "@COMPONENTS/style/detailpackage/productinformation";
import useEventTourDetail from "@QUERIES/bff/useEventTourDetail";
import useEventNotice from "@QUERIES/prdt/event/useEventNotice";
import useEventOptionalTourDetail from "@QUERIES/prdt/event/useEventOptionalTourDetail";
import useEventScheduleTrip from "@QUERIES/prdt/event/useEventScheduleTrip";
import React, {useCallback, useEffect, useState} from "react";
import useReviewSummary from "@QUERIES/portal/useReviewSummary";

interface Props {
	evCd: string;
	useScheduleView?: boolean;
}
interface TabType {
	title: string;
	contents: ({evCd}: {evCd: string}) => JSX.Element;
}

const ScheduleTab = (props: {evCd: string; tabs: TabType[]; isLast?: boolean}) => {
	const {evCd, tabs, isLast = false} = props;
	if (!tabs) return <></>;
	const [activeTab, setActiveTab] = useState(0);

	const ScrollToId = (id: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
		e.preventDefault();
		const element = document.getElementById(id);
		if (element) element.scrollIntoView({behavior: "smooth"});
	};

	const handleScroll = useCallback(() => {
		const contentsElements = Array.from(document.querySelectorAll("[id^=secondContents]"));
		contentsElements.forEach((el, index) => {
			if (el && window.scrollY + 166 > window.scrollY + el.getBoundingClientRect().top) {
				setActiveTab(index);
			}
		});

		if (!isLast) return;
		const scrollHeight = document.documentElement.scrollHeight;
		const scrollTop = document.documentElement.scrollTop;
		const clientHeight = document.documentElement.clientHeight;

		if (scrollTop + clientHeight >= scrollHeight) {
			setActiveTab(contentsElements.length - 1);
		}
	}, []);

	useEffect(() => {
		window.addEventListener("scroll", handleScroll);
		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, []);

	return (
		<>
			<style.ScrollTabList>
				{tabs.map((item, index) => (
					<li key={index}>
						<style.ScrollTabItem
							href={`secondContents${index}`}
							isActive={activeTab === index}
							onClick={ScrollToId(`secondContents${index}`)}
						>
							{item.title}
						</style.ScrollTabItem>
					</li>
				))}
			</style.ScrollTabList>
			{tabs.map((item, index) => {
				const Contents = item.contents;
				return (
					<style.ScrollContent id={`secondContents${index}`} key={index}>
						<Contents evCd={evCd} />
					</style.ScrollContent>
				);
			})}
		</>
	);
};

const ScheduleFirstComponent = ({evCd}: {evCd: string}) => {
	const goodsCd = evCd.substring(0, 7);
	const useNotice = useEventNotice(evCd);
	const [tabs, setTabs] = useState<TabType[]>([]);
	const notice = useNotice.data?.body;
	const useScheTrip = useEventScheduleTrip(evCd, goodsCd);

	useEffect(() => {
		if (useNotice.isFetching || useScheTrip.isFetching) return;
		const _tabs = [];
		if (notice) _tabs.push({title: "핵심정보", contents: Information});
		if (notice?.inclInfo || notice?.notinclInfo) _tabs.push({title: "포함/불포함/약관", contents: Notice});
		if (useScheTrip.data?.body?.scheduleDetail.length > 0) _tabs.push({title: "일차별 일정", contents: DailySchedule});
		setTabs(_tabs);
	}, [useNotice.isFetching, useScheTrip.isFetching]);

	return <>{tabs.length > 0 && <ScheduleTab tabs={tabs} evCd={evCd} />}</>;
};

/**
 * 핵심정보 | 포함/불포함 | 일차별 일정
 */
const ScheduleFirst = (props: {evCd: string}) => (
	<ErrorBoundary logMsg="DetailSchedule ScheduleFirst Error">
		<ScheduleFirstComponent {...props} />
	</ErrorBoundary>
);

/**
 * 옵션/선택관광 | 쇼핑 | 숙소 | 관광지 | 즐길거리 | 먹거리
 */
const ScheduleSecond = ({evCd}: {evCd: string}) => {
	const [tabs, setTabs] = useState<TabType[]>([]);
	const useEventOptTourDtl = useEventOptionalTourDetail(evCd);
	const useEventTourDtl = useEventTourDetail(evCd);

	useEffect(() => {
		if (useEventOptTourDtl.isFetching || useEventTourDtl.isFetching) return;
		const {accommodation, tourSpot, entertainment, food} = useEventTourDtl.data;
		const _tabs = [];
		_tabs.push({title: "옵션/선택관광", contents: Option});
		_tabs.push({title: "쇼핑", contents: Shopping});
		if (accommodation.show) _tabs.push({title: accommodation.title, contents: Accommodation});
		if (tourSpot.show) _tabs.push({title: tourSpot.title, contents: TourlistDestination});
		if (entertainment.show) _tabs.push({title: entertainment.title, contents: Enjoy});
		if (food.show) _tabs.push({title: food.title, contents: Food});
		setTabs(_tabs);
	}, [useEventOptTourDtl.isFetching, useEventTourDtl.isFetching]);

	return <>{tabs.length > 0 && <ScheduleTab tabs={tabs} evCd={evCd} />}</>;
};

/**
 * 참고/전달사항 | 유의 사항 | 약관 / 취소수수료 | 여권/비자 | 여행자보험 | 해외여행 안전정보
 */
const ScheduleThird = ({evCd}: {evCd: string}) => {
	const [tabs, setTabs] = useState<TabType[]>([]);
	const useNotice = useEventNotice(evCd);
	const notice = useNotice.data?.body;

	useEffect(() => {
		if (useNotice.isFetching) return;
		const _tabs = [];
		if (notice?.miscInfo || notice?.essenInfo) {
			_tabs.push({title: "참고/전달사항", contents: Refer});
		}
		_tabs.push({title: "유의 사항", contents: Importance});
		if (notice?.passportInfo) _tabs.push({title: "여권/비자", contents: PassportandVisa});
		_tabs.push({title: "여행자보험", contents: Insurance});
		_tabs.push({title: "해외여행 안전정보", contents: Safety});
		setTabs(_tabs);
	}, [useNotice.isFetching]);

	return <>{tabs.length > 0 && <ScheduleTab tabs={tabs} evCd={evCd} />}</>;
};

/** 고객리뷰 | 여행후기 */
const ScheduleFourth = ({evCd}: {evCd: string}) => {
	const tabs: TabType[] = [
		{title: "고객리뷰", contents: Review},
		{title: "여행후기", contents: Episode},
	];
	return <ScheduleTab tabs={tabs} evCd={evCd} isLast={true} />;
};

const DetailScheduleComponent = (props: Props) => {
	const {evCd, useScheduleView = false} = props;
	const [activeFirstTab, setActiveFirstTab] = useState(0);
	const reviewSummaryFetch = useReviewSummary(evCd.split("-")[0]);
	const reviewSummary = reviewSummaryFetch.data?.body;

	const [tabs, setTabs] = useState<TabType[]>([]);
	useEffect(() => {
		if (reviewSummaryFetch.isFetching) return;
		const _tabs = [];
		_tabs.push({title: "상품정보/일정", contents: ScheduleFirst});
		_tabs.push({title: "옵션/쇼핑/관광", contents: ScheduleSecond});
		_tabs.push({title: "참고사항", contents: ScheduleThird});
		!useScheduleView && _tabs.push({title: `리뷰 (${reviewSummary?.reviewCnt ?? 0})`, contents: ScheduleFourth});
		setTabs(_tabs);
	}, [reviewSummaryFetch.isFetching]);

	const scrollToTop = (index: number) => (e: React.MouseEvent<HTMLAnchorElement>) => {
		e.preventDefault();
		setActiveFirstTab(index);
		const element = document.querySelector(`#__scheduleTop`);
		element.scrollIntoView({behavior: "smooth"});
	};

	return (
		<>
			<style.Wrap id="__scheduleTop">
				<style.TopNavi>
					{tabs.map((item, index) => (
						<li key={index}>
							<style.TopNaviItem
								href={`#content${index}`}
								onClick={scrollToTop(index)}
								isActive={activeFirstTab === index}
							>
								{item.title}
							</style.TopNaviItem>
						</li>
					))}
				</style.TopNavi>
				{tabs.map((item, index) => {
					const Contents = item.contents;
					return (
						activeFirstTab === index && (
							<style.Content id={`content${index}`} key={index}>
								<Contents evCd={evCd} />
							</style.Content>
						)
					);
				})}
			</style.Wrap>
		</>
	);
};

const DetailSchedule = (props: Props) => (
	<ErrorBoundary logMsg="DetailSchedule Error">
		<DetailScheduleComponent {...props} />
	</ErrorBoundary>
);

export default DetailSchedule;
